'use client';

import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef } from 'react';
import {
  CHECKOUT_SUBPATH,
  ONBOARDING_INTERSTITIAL_SLUG,
  QUIZ_SUBPATH,
} from './routing';
import { getKeyFromPath } from './OnboardingFlowContainer/functions';
import { ROOT_SUBPATH } from './OnboardingFlowContainer/constants';

const INTERSTITIAL_STATE_KEY = `${QUIZ_SUBPATH}/${ONBOARDING_INTERSTITIAL_SLUG}`;

// When state machine changes the step, update URL to match
// This is very important, without changing the URL the new props for the page cannot be fetched.
export const useSyncPathToMachine = (
  routeChanging,
  isAppSession,
  stateKey,
  unmappedStates,
  pet_id,
  setShowPlanSelection,
  fromQuestion,
  setAnimationDone,
  // The flow's base path (with trailing slash), stripped from the URL to get
  // the current step. Defaults to /onboarding's.
  rootPath = ROOT_SUBPATH,
) => {
  const router = useRouter();

  // Keep a ref to always have the latest stateKey. This prevents a race condition
  // where onRouteChangeComplete fires before React commits the next render, causing
  // syncPathToMachine to use a stale stateKey from the previous closure.
  const stateKeyRef = useRef(stateKey);
  stateKeyRef.current = stateKey;

  const syncPathToMachine = useCallback(() => {
    if (!routeChanging.current) {
      const currentStateKey = stateKeyRef.current;
      // Derive the current step from router.asPath, which is updated
      // synchronously by the time routeChangeComplete fires. `pathname` from
      // router.query.path only refreshes on the post-navigation render, which
      // routeChangeComplete can beat — a stale value there defeats the
      // isAlreadyOnTargetPath guard and causes a duplicate router.push
      const currentPathname = getKeyFromPath(
        router.asPath?.split('?')[0] || '',
        rootPath,
      );
      const query = isAppSession ? { isAppSession } : {};

      // Prevent duplicate navigation: if the browser URL (router.asPath) is
      // already on the target step, skip the push.
      const currentPath =
        router.asPath?.split('?')[0]?.replace(/\/$/, '') || '';
      const targetPath = currentStateKey ? `/${currentStateKey}` : '';
      const asPathMatchesTarget =
        targetPath && currentPath.endsWith(targetPath);
      const isAlreadyOnTargetPath = Boolean(asPathMatchesTarget);

      // Skip navigation if already on the target path
      if (isAlreadyOnTargetPath) {
        return;
      }
      if (
        currentStateKey &&
        currentStateKey === CHECKOUT_SUBPATH &&
        currentPathname !== currentStateKey
      )
        router.push({
          pathname: `${currentStateKey}`,
          query,
        });
      else if (
        currentStateKey &&
        currentPathname !== currentStateKey &&
        !unmappedStates.includes(currentStateKey)
      ) {
        query.pet_id = pet_id;
        if (currentStateKey === INTERSTITIAL_STATE_KEY && fromQuestion) {
          query.from = fromQuestion;
        }
        router.push({
          pathname: `${currentStateKey}`,
          query,
        });
      }
    }
  }, [
    router,
    isAppSession,
    pet_id,
    routeChanging,
    unmappedStates,
    fromQuestion,
    rootPath,
  ]);

  useEffect(() => {
    syncPathToMachine();
    const onRouteChangeComplete = () => {
      routeChanging.current = false;
      syncPathToMachine();
      setShowPlanSelection(false);
      setAnimationDone?.(false);
    };
    // Reset routeChanging on error so it never gets stuck at true, which would
    // silently prevent all future syncPathToMachine navigations.
    const onRouteChangeError = () => {
      routeChanging.current = false;
    };

    router.events.on('routeChangeComplete', onRouteChangeComplete);
    router.events.on('routeChangeError', onRouteChangeError);
    return () => {
      router.events.off('routeChangeComplete', onRouteChangeComplete);
      router.events.off('routeChangeError', onRouteChangeError);
    };
  }, [
    syncPathToMachine,
    router,
    routeChanging,
    setShowPlanSelection,
    setAnimationDone,
    stateKey,
  ]);
};
